import CloseButton from 'engage/components/CloseButton/CloseButton';
import Modal from 'engage/components/Modal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './EditTemplateModal.module.scss';
import { CustomButton } from 'engage/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import Icon from 'engage/components/Icon/Icon';
import RadioButton from 'engage/components/RadioButton/RadioButton';
import CompanySelection from 'engage/components/CompanySelection/CompanySelection';
import VormatsAPI from 'common/apis/VormatsAPI';
import Spinner from 'common/components/Spinner';
import { ToastNotification } from 'engage/components/Toast/Toast';

const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 255;
const MAX_FILE_SIZE_MB = 5; // Maximum file size in MB

const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

function getAllowedExtensions(types) {
    const mimeToExt = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/webp': 'webp',
    };
    return types
        .map(type => mimeToExt[type] ? `.${mimeToExt[type]}` : type.split('/')[1] ? `.${type.split('/')[1]}` : type)
        .filter((ext, idx, arr) => arr.indexOf(ext) === idx)
        .join(', ');
}

export const EditTemplateModal = ({ companyId, open, onClose, template, onSave }) => {
    if (!open || !template) return null;
    const [newTitle, setNewTitle] = useState(template.name || '');
    const [newDescription, setNewDescription] = useState(template.description || '');
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    const history = useHistory();
    const [companyOption, setCompanyOption] = useState(template.isDefault ? 'default' : 'company');
    const [company, setCompany] = useState(null);
    const [companiesList, setCompaniesList] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchCompanies = useCallback(async () => {
        if (companiesList.length) {
            return companiesList;
        }
        const companies = await VormatsAPI.getCompaniesList();
        setCompaniesList(companies);
        return companies;
    }, [companiesList]);

    const handleMakeDefault = () => {
        setCompany(null);
    }

    const companyOptions = useMemo(() => [
        { id: 'default', title: 'advanced_editor.templates_overlay.create.is_default', description: 'advanced_editor.templates_overlay.create.is_default_description', icon: 'globe', action: handleMakeDefault },
        {
            id: 'company', title: 'advanced_editor.templates_overlay.create.per_company', description: 'advanced_editor.templates_overlay.create.per_company_description', icon: 'building', action: fetchCompanies
        },
    ], [handleMakeDefault, fetchCompanies]);

    const hasChanged = useMemo(() => {
        return newTitle.trim() !== template.name || newDescription.trim() !== template.description || file || (template.isDefault ? company !== null : companyId !== company?.id);
    }, [newTitle, newDescription, file, template.name, template.description, company]);

    const { t } = useTranslation();

    const thumbnailImage = useMemo(() => {
        if (file) {
            return URL.createObjectURL(file);
        }
        if (template.thumbnail) {
            return template.thumbnail;
        }
        return null;
    }, [file, template]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const updatedTemplate = {};
        if (newTitle.trim() !== template.name) {
            updatedTemplate.name = newTitle.trim();
        }
        if (newDescription.trim() !== template.description) {
            updatedTemplate.description = newDescription.trim();
        }
        if (file) {
            updatedTemplate.thumbnail = file;
        }
        if (company && (company.id !== companyId || template.isDefault)) {
            updatedTemplate.companyId = company.id;
        }
        if (template.isDefault && companyOption === 'default') {
            updatedTemplate.companyId = null;
        }
        try {
            setLoading(true);
            await onSave(template.id, updatedTemplate);
            ToastNotification({
                message: t('general.toast.templates.saving_successfully'),
                iconName: 'success',
            });
        } catch (error) {
            console.error("Error saving template:", error);
            ToastNotification({
                message: t('general.toast.templates.saving_error'),
            });
        } finally {
            setLoading(false);
            onClose();
        }
    }, [template, onSave, company, companyId, file, newTitle, newDescription, companyOption, onClose]);

    const handleFileChange = (event) => {
        if (!event.target.files) return;
        const file = event.target.files[0];
        if (!allowedTypes.includes(file.type)) {
            setError(t('advanced_editor.upload.error.type', { allowedTypes: getAllowedExtensions(allowedTypes) }));
            return;
        }
        if (!(file.size <= MAX_FILE_SIZE_MB * 1024 * 1024)) {
            setError(t('advanced_editor.upload.error.size', { max: MAX_FILE_SIZE_MB }));
            return;
        }
        setFile(file);
        setError(null);
    }

    const handleRedirect = useCallback(() => {
        if (newTitle.trim() !== template.name || newDescription.trim() !== template.description || file) {
            const confirm = window.confirm(t('company_settings.templates.confirm_redirect', { name: template.name }));
            if (!confirm) {
                return;
            }
        }
        history.push(`/eg/advanced-editor/templates/${template.id}`);
    }, [template, newTitle, newDescription, file]);

    function handleUploadClick() {
        inputRef.current?.click();
    }

    const handleCompanySelect = useCallback((company) => {
        setCompany(company);
    }, []);

    const handleOptionChanged = (option) => {
        setCompanyOption(option.id);
        if (option?.action) {
            option.action();
        }
    }

    useEffect(() => {
        const findCurrentCompany = async (companyId) => {
            const fetchedCompany = await fetchCompanies();
            const currentCompany = fetchedCompany.find(item => item.id === companyId);
            if (currentCompany) {
                setCompany(currentCompany);
            }
        }
        if (!template.isDefault && companyId) {
            findCurrentCompany(companyId);
        }
    }, [template.isDefault]);

    return (
        <Modal open={open} onClose={onClose}>
            <div className={cx(styles.wrapper, { [styles.loading]: loading })}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('company_settings.templates.edit_title')}</h2>
                    <CloseButton onClose={onClose} />
                </div>
                <div className={styles.inputContainer}>
                    <span className={styles.inputHeader}>{t('chapters_editor.placeholder')}</span>
                    <input
                        type="text"
                        className={cx(styles.infoInput, { [styles.invalidInput]: newTitle.trim().length == 0 })}
                        value={newTitle}
                        placeholder={t('chapters_editor.placeholder')}
                        onChange={event => setNewTitle(event.target?.value.trimStart())}
                        maxLength={TITLE_MAX_LENGTH}
                        size={TITLE_MAX_LENGTH}
                    />
                    <div className={styles.charCount}>{newTitle.length}/{TITLE_MAX_LENGTH}</div>
                </div>
                <div className={styles.inputContainer}>
                    <span className={styles.inputHeader}>{t('advanced_editor.templates_overlay.create.description_title')}</span>
                    <textarea
                        className={cx(styles.textareaInput)}
                        value={newDescription}
                        placeholder={t('my_video.description')}
                        onChange={event => setNewDescription(event.target?.value.trimStart())}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                    />
                    <div className={styles.charCount}>{newDescription.length}/{DESCRIPTION_MAX_LENGTH}</div>
                </div>
                {<div className={cx(styles.thumbnailContainer, { [styles.whiteBg]: !thumbnailImage })}>
                    {thumbnailImage ?
                        <img src={thumbnailImage} alt={t('company_settings.templates.thumbnail')} className={styles.thumbnail} />
                        : <Icon alt={'Icon for thumbnail placeholder'} src="thumbnailPlaceholder" className={cx(styles.thumbnail, styles.fallbackIcon)} />}
                    <CustomButton className={cx(styles.replaceButton)} variant='link' label={t(thumbnailImage ? 'advanced_editor.templates_overlay.create.replace_thumbnail' : 'advanced_editor.templates_overlay.create.upload_thumbnail')} onClick={handleUploadClick} />
                </div>}
                {error && <p className="text-sm text-red-500 m-0 px-2 pt-1.5">{error}</p>}
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    tabIndex={-1}
                    accept={allowedTypes.join(',')}
                    data-testid="upload-input"
                />
                <ul className={styles.companiesOptionsList}>
                    {companyOptions.map(option => <li key={option.id} className={styles.option}>
                        <div className={styles.optionHeader}>
                            <RadioButton type={'radiobutton'} checked={option.id === companyOption}
                                onChange={() => handleOptionChanged(option)} className={styles.radioBtn}
                            />
                            <Icon alt={`Icon for ${option.title}`} src={option.icon} className={styles.icon} />
                            <p className={styles.title}>{t(option.title)}</p>
                        </div>
                        <span className={styles.description}>{t(option.description)}</span>
                    </li>)}
                </ul>
                {companyOption === 'company' && (
                    <div style={{ width: '307px' }}>
                        <CompanySelection onSelect={handleCompanySelect} currentCompany={company} companies={companiesList} listOn='top' />
                    </div>
                )}
                <CustomButton variant='secondary' className={styles.redirect} onClick={handleRedirect}>
                    <Icon src='pencilRuler' className={styles.icon} />
                    {t('company_settings.templates.open_in_editor')}
                </CustomButton>
                <div className={styles.footer}>
                    <CustomButton variant='secondary' className={styles.controlBtn} onClick={onClose}>
                        {t('general.cancel')}
                    </CustomButton>
                    <CustomButton variant='primary' className={styles.controlBtn} disabled={!hasChanged || !(newTitle.trim())} onClick={handleSubmit}>
                        {t('general.save')}
                    </CustomButton>
                </div>
            </div>
            {loading && <Spinner className={styles.spinner} width='30px' height='30px' size='2px' />}
        </Modal>
    );
}
